import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileImage, FileText, Trash2, Upload, Eye, Loader2, X } from "lucide-react";
import { logisticsApi } from "@/lib/api";
import { toast } from "sonner";

const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = "image/jpeg,image/jpg,image/png,application/pdf";

function bytesLabel(n) {
  if (!n) return "—";
  if (n > 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} Mo`;
  return `${Math.round(n / 1024)} Ko`;
}

/**
 * Zone Médias (PDF / images) liée à une commande.
 * Upload + titre + commentaire + toggle "Afficher dans la fiche de chantier" + preview modale.
 */
export default function OrderMediaSection({ orderId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [showInWorksheet, setShowInWorksheet] = useState(true);
  const [file, setFile] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [blobCache, setBlobCache] = useState({}); // mediaId -> objectURL

  // Cleanup blob URLs on unmount
  useEffect(() => () => {
    Object.values(blobCache).forEach((u) => {
      try { URL.revokeObjectURL(u); } catch (_e) { /* noop */ }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getBlobUrl = useCallback(async (m) => {
    if (blobCache[m.id]) return blobCache[m.id];
    const res = await logisticsApi.fetchOrderMediaBlob(orderId, m.id);
    const url = URL.createObjectURL(res.data);
    setBlobCache((c) => ({ ...c, [m.id]: url }));
    return url;
  }, [blobCache, orderId]);

  const fetchMedia = useCallback(async () => {
    if (!orderId) return;
    try {
      setLoading(true);
      const res = await logisticsApi.listOrderMedia(orderId);
      setItems(res.data || []);
    } catch (e) {
      toast.error("Erreur de chargement des médias");
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { fetchMedia(); }, [fetchMedia]);

  const handleUpload = async (e) => {
    e?.preventDefault?.();
    if (!file) {
      toast.error("Sélectionne un fichier");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Fichier trop volumineux (max 10 Mo)");
      return;
    }
    try {
      setUploading(true);
      await logisticsApi.uploadOrderMedia(orderId, file, {
        title: title.trim() || undefined,
        comment: comment.trim() || undefined,
        show_in_worksheet: showInWorksheet,
      });
      toast.success("Média ajouté");
      setFile(null); setTitle(""); setComment(""); setShowInWorksheet(true);
      const input = document.getElementById("order-media-file-input");
      if (input) input.value = "";
      await fetchMedia();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'upload");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce média ?")) return;
    try {
      await logisticsApi.deleteOrderMedia(orderId, id);
      toast.success("Média supprimé");
      await fetchMedia();
    } catch (e) {
      toast.error("Erreur de suppression");
    }
  };

  const handleToggleShow = async (m) => {
    try {
      await logisticsApi.updateOrderMedia(orderId, m.id, { show_in_worksheet: !m.show_in_worksheet });
      await fetchMedia();
    } catch (e) {
      toast.error("Erreur de mise à jour");
    }
  };

  const handleEditMeta = async (m, key, value) => {
    try {
      await logisticsApi.updateOrderMedia(orderId, m.id, { [key]: value });
      await fetchMedia();
    } catch (e) {
      toast.error("Erreur de mise à jour");
    }
  };

  const fileUrl = (m) => blobCache[m.id] || "";

  // Preload blob URLs for newly added items
  useEffect(() => {
    items.forEach((m) => {
      if (!blobCache[m.id]) {
        getBlobUrl(m).catch(() => {});
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const openPreview = async (m) => {
    setPreviewItem(m);
    try {
      const url = await getBlobUrl(m);
      setPreviewUrl(url);
    } catch {
      toast.error("Erreur d'ouverture du fichier");
    }
  };

  return (
    <Card data-testid="order-media-section">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <FileImage className="h-4 w-4 text-blue-700" />
          Médias & documents
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          onSubmit={handleUpload}
          className="grid grid-cols-1 md:grid-cols-12 gap-2 p-3 border rounded-md bg-slate-50"
        >
          <div className="md:col-span-5">
            <Label className="text-xs">Fichier (PDF, JPG, PNG · 10 Mo max)</Label>
            <Input
              id="order-media-file-input"
              type="file"
              accept={ACCEPT}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="order-media-file-input"
            />
          </div>
          <div className="md:col-span-3">
            <Label className="text-xs">Titre</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Plan, photo avant…"
              data-testid="order-media-title"
            />
          </div>
          <div className="md:col-span-4">
            <Label className="text-xs">Commentaire</Label>
            <Input
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Notes optionnelles"
              data-testid="order-media-comment"
            />
          </div>
          <div className="md:col-span-9 flex items-center gap-2">
            <Switch
              checked={showInWorksheet}
              onCheckedChange={setShowInWorksheet}
              data-testid="order-media-show-worksheet"
            />
            <span className="text-xs text-slate-700">Afficher dans la fiche de chantier (PDF)</span>
          </div>
          <div className="md:col-span-3 flex items-end">
            <Button type="submit" disabled={uploading || !file} className="w-full" data-testid="order-media-upload-btn">
              {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
              Ajouter
            </Button>
          </div>
        </form>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground italic" data-testid="order-media-empty">
            Aucun média ajouté.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((m) => (
              <div
                key={m.id}
                className="border rounded-md overflow-hidden bg-white"
                data-testid={`order-media-card-${m.id}`}
              >
                <button
                  type="button"
                  className="block w-full h-32 bg-slate-100 flex items-center justify-center hover:bg-slate-200 cursor-pointer"
                  onClick={() => openPreview(m)}
                  data-testid={`order-media-preview-${m.id}`}
                >
                  {m.is_image ? (
                    <img
                      src={fileUrl(m)}
                      alt={m.title || m.filename}
                      className="object-cover w-full h-full"
                    />
                  ) : (
                    <div className="flex flex-col items-center text-slate-600">
                      <FileText className="h-10 w-10" />
                      <span className="text-xs mt-1">{m.filename}</span>
                    </div>
                  )}
                </button>
                <div className="p-2 space-y-2 text-sm">
                  <Input
                    value={m.title || ""}
                    onChange={(e) => {
                      const next = items.map((x) => x.id === m.id ? { ...x, title: e.target.value } : x);
                      setItems(next);
                    }}
                    onBlur={(e) => handleEditMeta(m, "title", e.target.value)}
                    placeholder="Titre…"
                    className="h-7 text-xs"
                    data-testid={`order-media-title-input-${m.id}`}
                  />
                  <Textarea
                    value={m.comment || ""}
                    onChange={(e) => {
                      const next = items.map((x) => x.id === m.id ? { ...x, comment: e.target.value } : x);
                      setItems(next);
                    }}
                    onBlur={(e) => handleEditMeta(m, "comment", e.target.value)}
                    placeholder="Commentaire…"
                    rows={2}
                    className="text-xs"
                    data-testid={`order-media-comment-input-${m.id}`}
                  />
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">{bytesLabel(m.size_bytes)}</span>
                    <div className="flex items-center gap-1">
                      <Switch
                        checked={!!m.show_in_worksheet}
                        onCheckedChange={() => handleToggleShow(m)}
                        data-testid={`order-media-toggle-${m.id}`}
                      />
                      <span className="text-[10px] text-slate-500">Fiche</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => openPreview(m)}
                      className="h-7 px-2"
                      data-testid={`order-media-open-${m.id}`}
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(m.id)}
                      className="h-7 px-2"
                      data-testid={`order-media-delete-${m.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={!!previewItem} onOpenChange={(o) => { if (!o) { setPreviewItem(null); setPreviewUrl(null); } }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{previewItem?.title || previewItem?.filename}</DialogTitle>
          </DialogHeader>
          {previewItem && previewUrl && (
            previewItem.is_image ? (
              <img
                src={previewUrl}
                alt={previewItem.title || previewItem.filename}
                className="w-full max-h-[70vh] object-contain"
              />
            ) : (
              <iframe
                src={previewUrl}
                title={previewItem.filename}
                className="w-full h-[70vh] border"
              />
            )
          )}
          {previewItem?.comment && (
            <p className="text-sm text-slate-700">{previewItem.comment}</p>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
